import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Skills = () => {

  const skills = [
            {
                "id": 1,
                "category" : "Languages & Frameworks",
                "skills" : ["PHP (10.5+ Years)", "Laravel (8.5+ Years)", "Livewire", "Vue.js", "React.js",
                    "Alpine.js", "CodeIgniter", "WordPress/WooCommerce", "JavaScript (ES6+)",
                    "jQuery", "Ajax", "HTML5", "CSS3", "Tailwind CSS", "Bootstrap"]
            },
            {
                "id": 2,
                "category" : "Databases & Storage",
                "skills" : ["MySQL (Advanced Query Optimization & Indexing)", "Redis (Caching, Queues)",
                    "AWS S3 (File Storage & CDN Integration)"]
            },
            {
                "id": 3,
                "category" : "Architecture & Patterns",
                "skills" : [
                    "MVC, Repository Pattern, Service Layer",
                    "REST API Design", "Microservices", "Monolithic to Modular Refactoring",
                    "Event-Driven Architecture", "Queues & Job Processing (Laravel Queues, Horizon)"
                ]
            },
            {
                "id": 4,
                "category" : "Cloud & DevOps",
                "skills" : [
                    "DigitalOcean (Droplets, App Platform)", "AWS (S3, EC2, RDS)",
                    "GitHub Actions (CI/CD)", "GitLab CI", "Bitbucket Pipelines", "LAMP & LEMP Stack" ,"TALL Stack"
                ]
            },
            {
                "id": 5,
                "category" : "Version Control & Collaboration",
                "skills" : ["Git (Branching, Rebase, Code Review)", "GitHub", "Bitbucket", "Trello"]
            },
            {
                "id": 6,
                "category" : "Payment Gateways",
                "skills" : [
                    "PayPal", "Stripe", "Authorize.net", "Razorpay"
                ]
            },
            {
                "id": 7,
                "category" : "APIs & Integrations",
                "skills" : ["Google APIs", "Facebook API", "Twitter API", "3rd Party SaaS Integrations", "Sign Now", "REST APIs", "JSON"]
            },
            {
                "id": 8,
                "category" : "Optimization & Best Practices",
                "skills" : [
                    "Database Normalization", "Caching Strategies (Redis, File, DB)",
                    "Code Refactoring", "Performance Profiling (Laravel Telescope, Debugbar)",
                    "Unit Testing & Feature Testing (PHPUnit, Pest)",
                    "Automation Jobs & Scheduling (Laravel Scheduler, CRON)"
                ]
            },
            {
                "id": 9,
                "category" : "Soft Skills & Team Contributions",
                "skills" : [
                    "Team Leadership & Mentorship", "Agile & Scrum",
                    "Client Communication", "Project Management",
                    "Technical Documentation"
                ]
            },
            {
                "id": 10,
                "category" : "Tools & Technologies",
                "skills" : ["Jira", "Monday", "ClickUp", "Project Management", "Team Leadership", "Client Communication"]
            }
        ];

  const skillColors = {
    "Languages & Frameworks": "bg-blue-500",
    "Databases": "bg-green-500",
    "Version Control": "bg-purple-500",
    "Payment Gateways": "bg-yellow-500",
    "APIs & Integrations": "bg-red-500",
    "Tools & Technologies": "bg-indigo-500"
  };

  return (
    <section id="skills" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Technical Skills
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            My expertise spans across various technologies and frameworks, 
            enabling me to build comprehensive web solutions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Skills Categories */}
          <div className="space-y-6">
            {skills.map((skillCategory, index) => (
              <motion.div
                key={skillCategory.id}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className={`w-4 h-4 ${skillColors[skillCategory.category] || 'bg-gray-500'} rounded-full mr-3`}></div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {skillCategory.category}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {skillCategory.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="bg-white text-gray-700 px-3 py-2 rounded-lg text-sm font-medium border hover:border-blue-300 transition-colors duration-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Skills Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="sticky top-24">
              <img
                src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHx3b3Jrc3BhY2V8ZW58MHx8fHwxNzU0MTM4MzE1fDA&ixlib=rb-4.1.0&q=85"
                alt="Development workspace"
                className="rounded-xl shadow-2xl w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent rounded-xl"></div>
              
              {/* Experience Stats */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">10.5</div>
                      <div className="text-sm text-gray-600">Years PHP</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">8.5</div>
                      <div className="text-sm text-gray-600">Years Laravel</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional Skills Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 bg-blue-50 p-8 rounded-xl"
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Professional Capabilities
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Full-Stack Development
              </h4>
              <p className="text-gray-600 text-sm">
                End-to-end web application development with modern frameworks
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Team Leadership
              </h4>
              <p className="text-gray-600 text-sm">
                Experience in team handling and project management
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Client Communication
              </h4>
              <p className="text-gray-600 text-sm">
                Strong communication skills for requirement gathering
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;